import { Button, Callout, Flex, Icon, Link, Text } from '@scale/scaleui-radix';
import {
  faDownload,
  faInfoCircle,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { CohortDTO } from '@scale/cohorts';

const MAX_COHORT_SIZE = 1_000_000;

export const EvaluationResultsSection = ({
  potentialContributorCount,
  exactMatchCohort,
  onExistingCohortClicked,
  onDownloadCSVClicked,
  isDownloadingCSV,
  inflowCount,
  outflowCount,
  isEditMode,
}: {
  potentialContributorCount: number;
  exactMatchCohort: CohortDTO | undefined;
  onExistingCohortClicked: (cohortId: string) => void;
  onDownloadCSVClicked: () => void;
  isDownloadingCSV: boolean;
  inflowCount?: number;
  outflowCount?: number;
  isEditMode?: boolean;
}) => {
  const showInflowOutflow = isEditMode && inflowCount !== undefined && outflowCount !== undefined;
  const exceedsMaxSize = potentialContributorCount >= MAX_COHORT_SIZE;

  return (
    <>
      <Flex direction="row" gap="3" align="center">
        {typeof potentialContributorCount === 'number' && potentialContributorCount >= 0 && (
          <Callout.Root
            size="1"
            className="w-full"
            style={{ justifyContent: 'unset' }}
            color={exceedsMaxSize ? 'error' : undefined}
          >
            <Flex direction="row" gap="2" align="center" justify="between">
              <Flex direction="row" gap="2" align="center">
                <Callout.Icon>
                  <Icon
                    icon={exceedsMaxSize ? faTriangleExclamation : faInfoCircle}
                    label={exceedsMaxSize ? 'Error' : 'Cohort owner'}
                    size={exceedsMaxSize ? '4' : '2'}
                  />
                </Callout.Icon>
                <Callout.Text>
                  {exceedsMaxSize ? (
                    <>
                      {`${potentialContributorCount.toLocaleString()} contributors matched these conditions, which exceeds the maximum cohort size of ${MAX_COHORT_SIZE.toLocaleString()}. `}
                      Please refine your filters to reduce the cohort size, or reach out to{' '}
                      <strong>@allocation-eng-team</strong> on slack with a request to create this
                      cohort.
                    </>
                  ) : (
                    <>
                      {`${potentialContributorCount.toLocaleString()} ${
                        potentialContributorCount === 1 ? 'contributor' : 'contributors'
                      } matched these conditions.`}
                      {showInflowOutflow && (
                        <>{` ${outflowCount.toLocaleString()} will be removed and ${inflowCount.toLocaleString()} will be added after editing.`}</>
                      )}
                    </>
                  )}
                </Callout.Text>
              </Flex>
              {!exceedsMaxSize && (
                <Button
                  variant="outline"
                  size="1"
                  onClick={onDownloadCSVClicked}
                  loading={isDownloadingCSV}
                >
                  <Icon size="1" color="accent" icon={faDownload} label="CSV" />
                  <Text>CSV</Text>
                </Button>
              )}
            </Flex>
          </Callout.Root>
        )}
      </Flex>
      {exactMatchCohort && (
        <Flex direction="row" gap="2">
          <Icon icon={faTriangleExclamation} label="Error" size="1" color="error" />
          <Text size="1" color="error">
            A cohort named{' '}
            <Link
              underline="always"
              onClick={() => {
                onExistingCohortClicked(exactMatchCohort._id);
              }}
              href="#"
            >
              {exactMatchCohort?.name}
            </Link>
            {''} already exists with the same members. Are you sure you want to create a new cohort
            with the same members?
          </Text>
        </Flex>
      )}
    </>
  );
};
